import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ITrainer, ITrainerInput } from "models/trainer";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import BlockIcon from "@mui/icons-material/Block";
import {
  TrainerCells,
  addTrainer,
  deleteTrainer,
  editTrainer,
  getTrainers,
} from "framework/trainer";

import Search from "components/Search/Search";
import AddTrainer from "./partials/AddTrainer/AddTrainer";
import EditTrainer from "./partials/EditTrainer/EditTrainer";
import DeleteTrainer from "./partials/DeleteTrainer/DeleteTrainer";
import { IVendor } from "models/vendor";
import { getVendors } from "framework/vendor";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission, getUserData, isValidEmailRFC5322 } from "helpers";
import phone from "phone";

interface Props {
  feature: Feature;
}

const Trainers: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [loading4, setLoading4] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [Trainers, setTrainers] = useState<ITrainer[]>([] as ITrainer[]);
  const [FilterTrainers, setFilterTrainers] = useState<ITrainer[]>(
    [] as ITrainer[]
  );
  const [Vendors, setVendors] = useState<IVendor[]>([] as IVendor[]);
  const user = getUserData();
  const { colors } = user;
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedTrainer, setSelectedTrainer] = useState<ITrainer>(
    {} as ITrainer
  );
  const [inputObj, setInputObj] = useState<ITrainerInput>({} as ITrainerInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addTrainersMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addTrainer(createInput);
    },
  });

  const editTrainersMutation = useMutation({
    mutationFn: (data: { createInput: FormData; id: string }) => {
      return editTrainer(data.createInput, data.id);
    },
  });

  const deleteTrainersMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteTrainer(code);
    },
  });

  function isValidNumber(countryCodeNumeric: string): boolean {
    const { isValid } = phone(countryCodeNumeric);
    return isValid;
  
  }

  const fetchVendors = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Vendors", () =>
        getVendors()
      );
      setVendors(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchData = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Trainers", () =>
        getTrainers()
      );

      setTrainers(
        response.map((item: ITrainer) => ({
          ...item,
          logo: item.logo,
        }))
      );
      setFilterTrainers(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    try {
      const res = await getPermission(3);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchVendors();
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3 || loading4)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () =>{setOpenAdd(false);setInputObj({} as ITrainerInput);} 

  const handleOpenEdit = (Trainer: ITrainer) => {
    setSelectedTrainer(Trainer);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setInputObj({} as ITrainerInput)
    setSelectedTrainer({} as ITrainer);
  };

  const handleOpenDelete = (Trainer: ITrainer) => {
    setSelectedTrainer(Trainer);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedTrainer({} as ITrainer);
  };

  const getBodyCells = (row: ITrainer) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "logo")
        return {
          id: "Logo",
          element: (
            <TableCell align={"left"} key={i}>
              {row.logo ? (
                <Box
                  component={"img"}
                  src={row?.logo}
                  alt={row?.firstName}
                  width={50}
                  height={50}
                />
              ) : (
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {row?.firstName?.charAt(0)}
                </Avatar>
              )}
            </TableCell>
          ),
        };
      if (key === "firstName")
        return {
          id: "FirstName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.firstName : row.firstNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "middleName")
        return {
          id: "MiddleName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.middleName : row?.middleNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "lastName")
        return {
          id: "LastName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.lastName : row?.lastNameAr}
              </Typography>
            </TableCell>
          ),
        };

        if (key === "email")
          return {
            id: "email",
            element: (
              <TableCell align={"left"} key={i}>
                <Typography variant="body2">
                  {row?.email}
                </Typography>
              </TableCell>
            ),
          };
          if (key === "countryCode")
            return {
              id: "countryCode",
              element: (
                <TableCell align={"left"} key={i}>
                  <Typography variant="body2">
                  {row?.countryCode}
                  </Typography>
                </TableCell>
              ),
            };
          if (key === "phoneNumber")
            return {
              id: "phoneNumber",
              element: (
                <TableCell align={"left"} key={i}>
                  <Typography variant="body2">
                  {row?.phoneNumber}
                  </Typography>
                </TableCell>
              ),
            };
      if (key === "biography")
        return {
          id: "Biography",
          element: (
            <TableCell align={"left"} key={i}>
              <Tooltip title={row?.biography}>
                <Typography variant="body2">
                  {row?.biography.split(" ").slice(0, 100).join(" ") + "..."}
                </Typography>
              </Tooltip>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.FirstName ||
      !inputObj.LastName ||
      !inputObj.Email ||
      !inputObj.CountryCode ||
      !inputObj.PhoneNumber ||
      !inputObj.Biography ||
      !inputObj.Logo 
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });

      if (
      !isValidEmailRFC5322(inputObj.Email) 
    )
      return setAlert({
        open: true,
        message: "Email is not valid",
        type: "error",
      });

      if (
      !isValidNumber(inputObj.CountryCode + inputObj.PhoneNumber))
      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });

    try {
      setLoading2(true);
      const createInput = new FormData();
      createInput.append("FirstName", inputObj.FirstName);
      if(inputObj.MiddleName)createInput.append("MiddleName", inputObj.MiddleName);
      createInput.append("LastName", inputObj.LastName);
      if(inputObj.FirstNameAr)createInput.append("FirstNameAr", inputObj.FirstNameAr);
      if(inputObj.MiddleNameAr)createInput.append("MiddleNameAr", inputObj.MiddleNameAr);
      if(inputObj.LastNameAr)createInput.append("LastNameAr", inputObj.LastNameAr);
      createInput.append("Biography", inputObj.Biography);
      createInput.append("Email", inputObj.Email);
      createInput.append("CountryCode", inputObj.CountryCode);
      createInput.append("PhoneNumber", inputObj.PhoneNumber);
      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo[0] || inputObj.Logo );
      await addTrainersMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Trainer added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      const createInput = new FormData();
      if (inputObj.FirstName)createInput.append("FirstName",inputObj.FirstName);
      if(inputObj.MiddleName || inputObj.MiddleName === null)createInput.append("MiddleName",inputObj.MiddleName||"" );
      if (inputObj.LastName)createInput.append("LastName",inputObj.LastName);
      if(inputObj.FirstNameAr || inputObj.FirstNameAr === null)createInput.append("FirstNameAr",inputObj.FirstNameAr || "");
        if (inputObj.Email){
          if (!isValidEmailRFC5322(inputObj.Email))
            return setAlert({
              open: true,
              message: "Email is not valid",
              type: "error",
            });
             createInput.append(
            "Email",
            inputObj.Email 
          );
        }
         
          if (inputObj.CountryCode)
            createInput.append(
              "CountryCode",
              inputObj.CountryCode
            );
            if (inputObj.PhoneNumber){
              if (
      !isValidNumber((inputObj.CountryCode || selectedTrainer.countryCode) + (inputObj.PhoneNumber || selectedTrainer.phoneNumber)))


      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });
      createInput.append(
                "PhoneNumber",
                inputObj.PhoneNumber 
              );
            }
              
      if(inputObj.MiddleNameAr || inputObj.MiddleNameAr ===null)createInput.append("MiddleNameAr",inputObj.MiddleNameAr ||"");
      if(inputObj.LastNameAr || inputObj.LastNameAr===null)createInput.append("LastNameAr",inputObj.LastNameAr||"");
      if (inputObj.Biography)
        createInput.append(
          "Biography",
          inputObj.Biography || selectedTrainer.biography
        );

      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo[0]||inputObj.Logo);
      await editTrainersMutation.mutateAsync({
        createInput,
        id: selectedTrainer.id,
      });
      setAlert({
        open: true,
        message: "Trainer updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteTrainersMutation.mutateAsync(selectedTrainer.id);
      setAlert({
        open: true,
        message: "Trainer deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterTrainers(Trainers);
    const search = Trainers.filter((item) =>
      (item.firstName + " " + item.middleName + " " + item.lastName)
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilterTrainers(search);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_trainer")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterTrainers.length > 0 && (
          <List
            headCells={TrainerCells}
            data={FilterTrainers}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterTrainers || FilterTrainers.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_training_header")}
              message={t("T_no_training_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddTrainer
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          Vendors={Vendors}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditTrainer
          open={openEdit}
          loading={loading2}
          Trainer={selectedTrainer}
          Vendors={Vendors}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteTrainer
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Trainers;
